import { Injectable } from '@angular/core';
import { AutoCompleteService, GrupoResposta, HttpClient, moment, NumberUtils, Page, PageParameters } from '@elotech/angular-common';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { FuncionarioDTO } from '../acao-coletiva/acao-coletiva-mudanca-lotacao/table/acao-coletiva-mudanca-lotacao-funcionario.service';
import { PortalServidor } from '../common/annotations/portal-servidor';
import { FuncionarioNotificationDto } from '../common/components/funcionario-delete/funcionario-notification-dto';
import { SelecaoMatrizSalarial } from '../common/components/selecao-matriz-salarial/selecao-matriz-salarial.model';
import { EnumItem } from '../common/enum-item';
import { FuncionarioOption } from "../common/interface/funcionario-option";
import { FuncionarioQuadroSalarial } from '../common/interface/funcionario-quadro-salarial';
import { FuncionarioReduzido } from '../common/interface/funcionario-reduzido';
import { TableServidorReduzido } from "../common/interface/table-servidor-reduzido";
import { EsocialService } from '../common/services/esocial-service';
import { ConvenioBancario } from '../convenio-bancario/convenio-bancario';
import { Funcionario } from '../lancamento/model';
import { Produtividade } from '../produtividade/produtividade';
import { FuncionarioSimplificadoDTO } from './FuncionarioSimplificadoDTO';
import { FuncionarioPlainDTO } from "../common/interface/funcionario-plain-dto";
import { FuncionarioViewDto } from '../common/interface/funcionario-view-dto';

@Injectable()
export class FuncionarioService extends EsocialService<any> implements AutoCompleteService {
  constructor(public httpClient: HttpClient) {
    super(httpClient);
    this.resource = '/api/funcionarios';
  }

  public loadFieldModels(fields: string = ''): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/custom/model`,
      fields
    });
  }

  findToOptions(searchValue: string): Observable<any[]> {
    return this.findAllReduzido(
      new PageParameters(15, 0, 'matricula,asc'),
      this.getSearch(searchValue),
      this.fields.toString(),
      true
    ).pipe(map((page) => page.content));
  }

  getSearch(searchValue) {
    return `(pessoa.nome == "*${searchValue}*" ${
      NumberUtils.isNumeric(searchValue) ? ', matricula == ' + searchValue : ''
    })`;
  }

  objToOption(funcionario: any): string {
    if (!funcionario?.matricula) {
      return '';
    }

    const nome = funcionario.pessoa?.nome || funcionario.nome || '';
    return nome ? `${funcionario.matricula} - ${nome}` : `${funcionario.matricula}`;
  }

  public objToOptionPlainDTO() {
    return (funcionario: FuncionarioPlainDTO) => `${funcionario.matricula} - ${funcionario.nome}`;
  }

  findToOptionFuncionarioEPrevidenciaNotDesligado(searchValue: string): Observable<any[]> {
    return this.findAll(
      new PageParameters(15, 0, 'matricula,asc'),
      this.getSearch(searchValue) + ';situacaoFuncional.situacao != DESLIGADO',
      'id,dataAdmissao,dataDesligamento,tipoCadastro,pessoa.id,pessoa.nome,matricula,classe.id,classe.codigo,classe.descricao,classe.previdencia',
      true
    ).pipe(map((page) => page.content));
  }

  findToOptionsNotDesligado(searchValue: string) {
    return this.findAllReduzido(
      new PageParameters(20, 0, 'matricula,asc'),
      this.getSearch(searchValue) + ';situacaoFuncional.situacao != DESLIGADO'
    ).pipe(map((page) => page.content));
  }

  getVidaFuncional(
    id: number,
    paginacao: any,
    search: string,
    disableSpinner: boolean
  ): Observable<Page<any>> {
    const requestParams = new PageParameters(
      paginacao.maxResult,
      paginacao.page,
      'data,desc'
    );

    return this.httpClient.get({
      disableSpinner: disableSpinner,
      search,
      page: requestParams,
      resource: `${this.resource}/vida-funcional/${id}`,
    });
  }

  public getMatricula(): Observable<number> {
    return this.httpClient.get({
      disableSpinner: true,
      resource: `${this.resource}/matricula`,
    });
  }

  public isMatriculaJaCadastrada(id: number, matricula: number): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/matricula-ja-cadastrada/${id ?? 0}/${matricula}`,
      disableSpinner: true,
    });
  }

  public findFuncionariosBarraLateral(search: string): Observable<any> {
    search = encodeURI(search);
    return this.httpClient.get({
      resource: `${this.resource}/filter-barra-lateral?search=${search}`,
      disableSpinner: true,
    });
  }

  @PortalServidor()
  public findFuncionariosPorCpf(cpf: string): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/funcionarios-por-cpf/${cpf}`,
      disableSpinner: true,
    });
  }

  public findToOptionsProjectionSlice(search: string, filterDefault?: string): Observable<FuncionarioPlainDTO[]> {
    return this.findByRsqlProjectionSlice(
      new PageParameters(20, 0, 'matricula,asc'),
      this.getSearch(search) + (filterDefault ? ';' + filterDefault : ''),
      '',
      true)
      .pipe(
        map(page => page.content)
      );
  }

  public findToOptionsProjectionSliceNotDesligado(search: string): Observable<FuncionarioPlainDTO[]> {
      return this.findToOptionsProjectionSlice(search, 'situacaoFuncional.situacao != DESLIGADO');
  }

  public getTotalFuncionarios(
    situacao: any,
    naCompetencia = false
  ): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/total-por-situacao?naCompetencia=${naCompetencia}`,
      search: situacao,
      disableSpinner: true,
    });
  }

  public isClasseUtilizada(idClasse: number): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/classe-utilizada`,
      id: idClasse,
      disableSpinner: true,
    });
  }

  public contarFuncionariosNaEstrutura(idEstrutura: number, data?: Date) {
    const dataParam = data ? `?data=${data}` : '';
    return this.httpClient.get({
      resource: `${this.resource}/estrutura-organizacional/${idEstrutura}${dataParam}`,
    });
  }

  existeFuncionarioForaDoIntervalo(
    planoCargoId: number,
    selecao: SelecaoMatrizSalarial
  ): Observable<ForaDoIntervaloDTO> {
    return this.httpClient.post({
      resource: `${this.resource}/existe-fora-do-intervalo`,
      body: {
        planoCargoId,
        selecaoInicial: selecao.inicial,
        selecaoFinal: selecao.final,
      },
    });
  }

  loadCargoNomeado(funcionarioId: number): Observable<any> {
    if (!funcionarioId) {
      return of();
    }
    return this.httpClient.get({
      resource: `${this.resource}/cargo-nomeado/${funcionarioId}`,
    });
  }

  post(entity: any, fields?: string): Observable<any> {
    return this.httpClient.post({
      resource: `${this.resource}/dto`,
      body: entity,
      fields: this.calculeFields(fields),
      location: this.location,
    });
  }

  put(id: number, entity: any, fields?: string): Observable<any> {
    return this.httpClient.put({
      resource: `${this.resource}/dto`,
      body: entity,
      id,
      fields: this.calculeFields(fields),
      location: this.location,
    });
  }

  remove(id: number): Observable<FuncionarioNotificationDto> {
    return this.httpClient.del({
      resource: `${this.resource}/dto`,
      id,
      location: this.location,
    });
  }

  findOne(
    id: number | string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/dto`,
      id,
      fields: this.calculeFields(fields),
      location: this.location,
      disableSpinner,
    });
  }

  findById(id: number | string, fields?: string): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}`,
      location: this.location,
      id,
      fields,
    });
  }

  public existsByPostoTrabalhoId(id: number): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/exists-by-posto-trabalho/${id}`,
      disableSpinner: true,
    });
  }

  findToOptionsFuncionarioPlainDTOComDireitoFerias(search: string, listarDesligados: boolean = true): Observable<FuncionarioPlainDTO[]> {
    return this.httpClient.get({
      resource: `${this.resource}/com-direito-ferias?listarDesligados=${listarDesligados}`,
      search: this.buildSearch(this.getSearch(search)),
      disableSpinner: true,
      page: new PageParameters(15, 0, 'matricula,asc')
    }).pipe(
      map(page => page.content)
    );
  }

  findAll(
    page?: PageParameters,
    search?: string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<Page<FuncionarioDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}`,
      page,
      search: this.buildSearch(search),
      disableSpinner,
      fields: this.calculeFields(fields),
    });
  }

  public findByRsqlProjection(
    page?: PageParameters,
    search?: string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<Page<FuncionarioPlainDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}/find-by-rsql-projection`,
      page,
      search: this.buildSearch(search),
      disableSpinner,
      fields: this.calculeFields(fields),
    });
  }

  public findByRsqlProjectionSlice(
    page?: PageParameters,
    search?: string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<Page<FuncionarioPlainDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}/find-by-rsql-projection-slice`,
      page,
      search: this.buildSearch(search),
      disableSpinner,
      fields: this.calculeFields(fields),
    });
  }

  public findOneDTO(id: number): Observable<FuncionarioDTO> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/${id}`
    });
  }

  public findOneDTOMovimentacaoPessoal(matricula?: string): Observable<FuncionarioDTO> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/movimentacao-pessoal-by-matricula?matricula=${matricula}`
    });
  }

  findDataFimNomeacaoTemporaria(funcionarioId: number): Observable<string | null> {
    return this.httpClient.get({
      resource: `${this.resource}/data-fim-nomeacao-temporaria/${funcionarioId}`
    });
  }

  findAllReduzido(
    page?: PageParameters,
    search?: string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<Page<FuncionarioDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/reduzido`,
      page,
      search: this.buildSearch(search),
      fields: this.calculeFields(fields),
      disableSpinner,
    });
  }

  findOneDTOReduzidoByMatricula(matricula: string): Observable<FuncionarioDTO> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/reduzido-by-matricula?matricula=${matricula}`
    });
  }

  findOneFuncionarioViewDTO(id: number): Observable<FuncionarioViewDto> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/funcionarioViewDto/${id}`
    });
  }

  findAllReduzidoAutocomplete(
    page?: PageParameters,
    search?: string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<Page<FuncionarioDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/reduzido/autocomplete`,
      page,
      search: this.buildSearch(search),
      fields: this.calculeFields(fields),
      disableSpinner,
    });
  }

  findAllFuncionarioLancamentoDto(
    page?: PageParameters,
    search?: string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<Page<FuncionarioDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}/funcionarioLancamentoDto`,
      page,
      search: this.buildSearch(search),
      disableSpinner,
    });
  }

  findFuncionarioParcelaById = (
    id: number
  ): Observable<FuncionarioParcelaDTO> => {
    return this.httpClient.get({
      resource: `/api/funcionarioparcela/${id}`,
      disableSpinner: true,
    });
  };

  existsByConvenioBancario(id: any): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/exists/by-convenio/${id}`,
      disableSpinner: true,
    });
  }

  updateConvenioBancario(
    funcionario: any,
    convenio: any
  ): Observable<ConvenioBancario> {
    return this.httpClient.put({
      resource: `${this.resource}/update/convenio/${funcionario.id}`,
      body: convenio,
      disableSpinner: true,
    });
  }

  findByProdutividade(produtividadeId: number): Observable<Funcionario> {
    return this.httpClient.get({
      resource: `${this.resource}/produtividade/${produtividadeId}`,
      disableSpinner: true,
    });
  }

  updateProdutividade(funcionariosId: FuncionarioReduzido[], produtividade: Produtividade): Observable<void> {
    return this.httpClient.put({
      resource: `${this.resource}/produtividade/${produtividade.id}`,
      body: funcionariosId,
    });
  }

  findByCondicoesAmbientaisDoTrabalho(
    id: number,
    page: PageParameters
  ): Observable<Page<CondicoesAmbientaisFuncionario>> {
    return this.httpClient.get({
      resource: `${this.resource}/by-condicoes-ambientais-de-trabalho/${id}`,
      page,
    });
  }

  findFuncionarioLancamentoDTO(
    id: number,
    competenciaId: number,
    disableSpinner?: boolean
  ): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/funcionario-lancamento/${id}/${competenciaId}`,
      disableSpinner
    });
  }

  findOneFuncionarioSimplificadoListarDTOByDataInicio(id: number, dataInicio: Date): Observable<FuncionarioSimplificadoDTO> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/find-one-funcionario-simplificado/${id}?dataInicio=${dataInicio}`
    });
  }

  getRespostasAgrupadas(
    funcionalidade: string,
    referenciaId?: string,
    cargoId?: number,
    lotacaoId?: number,
    classeId?: number,
    exibirVidaFuncional?: boolean,
  ): Observable<GrupoResposta[]> {
    const filtroVidaFuncional = exibirVidaFuncional ? `&exibirVidaFuncional=${exibirVidaFuncional}` : '';
    return this.httpClient.get({
      resource: `/api/funcionario-campodinamico/respostas-agrupadas?funcionalidade=${funcionalidade}&referenciaId=${referenciaId}&cargoId=${cargoId}&lotacaoId=${lotacaoId}&classeId=${classeId}${filtroVidaFuncional}`,
    });
  }

  findFuncionariosByPessoaId(pessoaId: number, isEditOrDelete: boolean): Observable<Funcionario[]> {
    return this.httpClient.get({
      resource: `${this.resource}/funcionarios-por-pessoa/${pessoaId}/${isEditOrDelete}`,
    });
  }

  funcionarioHasPrevidenciaRgps(funcionarioId: number): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/has-previdencia-rgps/${funcionarioId}`,
    });
  }

  findAutoCompleteSimplificado(
    search?: string
  ): Observable<Page<any>> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/autocomplete-simplificado`.concat(search ? `?search=${search}` : ''),
      page: new PageParameters(15, 0, 'matricula,asc')
    }).pipe(
      map(page => page.content)
    );
  }

  existsByCpfAndFuncionarioId(cpf: string, funcionarioId: number): Observable<boolean> {
    if (!cpf || !funcionarioId) {
      return of(false);
    }

    return this.httpClient.get({
      resource: `${this.resource}/exists-by-cpf-and-funcionarioid?cpf=${cpf}&funcionarioId=${funcionarioId}`,
      disableSpinner: true,
    });
  }

  public findFuncionariosHoristasCompetencia(competenciaId: number): Observable<FuncionarioOption[]> {
    return this.httpClient.get({
      resource: `${this.resource}/horistas-competencia/${competenciaId}`,
    });
  }

  public findFuncionariosQuadroSalarialByCargoId(cargoId: number, search: string, somenteAtivos: Boolean, somenteDesligados: Boolean, page: PageParameters): Observable<Page<FuncionarioQuadroSalarial>> {
    return this.httpClient.get({
      resource: `${this.resource}/funcionario-plano-salarial/cargo/${cargoId}?search=${search}&somenteAtivos=${somenteAtivos}&somenteDesligados=${somenteDesligados}`,
      disableSpinner: true,
      page: page,
    });
  }

  public isFuncionarioNomeadoById(funcionarioId: number): Observable<boolean>  {
    return this.httpClient.get({
      resource: `${this.resource}/is-funcionario-nomeado/${funcionarioId}`,
      disableSpinner: false,
    })
  }

  public getMatriculasDuploVinculoByPessoaId(pessoaId: number): Observable<number[]>{
    return this.httpClient.get({
      resource: `${this.resource}/matriculas-duplo-vinculo/${pessoaId}`,
      disableSpinner: false,
    });
  }

  public possuiMatriculaPrincipal(matriculas: number[]): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/possui-matricula-principal?matriculas=${matriculas.join(',')}`,
      disableSpinner: false,
    });
  }

  public findAllTableServidor(matriculas: string, search?: string, page?: PageParameters): Observable<Page<TableServidorReduzido>> {
    return this.httpClient.post({
      resource: `${this.resource}/dto/table-servidor`,
      body: !!matriculas ? matriculas : '0',
      page: page,
      search: this.buildSearch(search),
    });
  }

  public possuiFichaOuMovimentacao(funcionarioId: any) {
    return this.httpClient.get({
      resource: `${this.resource}/possui-ficha-ou-movimentacao/${funcionarioId}`
    });
  }

  public getFilterFuncionarioAtivosNaCompetencia(inicioCompetencia: string): string {
    const fimCompetencia = moment(inicioCompetencia).endOf('month').format('YYYY-MM-DD');
    return `(
      situacaoFuncional.situacao==DESLIGADO;
      dataDesligamento<=${fimCompetencia};dataDesligamento>=${inicioCompetencia},
      situacaoFuncional.situacao!=DESLIGADO;
      tipoCadastro==FUNCIONARIO
    )`;
  }

}


export interface CondicoesAmbientaisFuncionario {
  id: number;
  funcionarioId: number;
  situacao: string;
  matricula: string;
  nome: string;
  cnpjCpf: string;
  codigoLotacao: string;
  cargo: string;
  classe: string;
  statusEsocial: EnumItem;
  tipoCadastro: EnumItem;
}

export interface ForaDoIntervaloDTO {
  existeForaDoIntervalo: boolean;
  funcionariosId: number[];
}

export interface FuncionarioParcelaDTO {
  id: number;
  parcela: number;
  competenciaCalculo: string;
  dataPagamento: Date;
  valorParcela: number;
}
